import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { useRazao } from "@/features/contabil/hooks/useContabilizacao";
import { usePlanoContas } from "@/features/contabil/hooks/usePlanoContas";
import { TIPO_OPERACAO_LABEL, type TipoOperacaoContabil } from "@/features/contabil/types/contabilizacao";

const fmtCur = (v: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);

export default function RazaoPage() {
  const { plano: contas = [] } = usePlanoContas();
  const [contaId, setContaId] = useState<number>(0);
  const [dtInicio, setDtInicio] = useState(new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10));
  const [dtFim, setDtFim] = useState(new Date().toISOString().slice(0, 10));

  const { data, isLoading } = useRazao(contaId, dtInicio, dtFim);

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Livro Razão</h1>
        <p className="text-muted-foreground">Lançamentos por conta com saldo acumulado.</p>
      </div>

      <Card>
        <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Conta contábil</Label>
            <Select value={String(contaId)} onValueChange={(v) => setContaId(Number(v))}>
              <SelectTrigger><SelectValue placeholder="Selecione…" /></SelectTrigger>
              <SelectContent>
                {contas.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {c.codigoPlanoConta} — {c.descricaoConta}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>De</Label>
            <Input type="date" value={dtInicio} onChange={(e) => setDtInicio(e.target.value)} />
          </div>
          <div>
            <Label>Até</Label>
            <Input type="date" value={dtFim} onChange={(e) => setDtFim(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      {isLoading ? <Skeleton className="h-48" /> : !data ? null : (
        <Card>
          <CardHeader>
            <CardTitle>{data.codigoConta} — {data.descricaoConta}</CardTitle>
            <p className="text-sm text-muted-foreground">
              Saldo inicial: <strong>{fmtCur(data.saldoInicial)}</strong> ·
              Total D: <strong>{fmtCur(data.totalDebitos)}</strong> ·
              Total C: <strong>{fmtCur(data.totalCreditos)}</strong> ·
              Saldo final: <strong className={data.saldoFinal >= 0 ? "text-green-600" : "text-red-600"}>
                {fmtCur(data.saldoFinal)}
              </strong>
            </p>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Lanç.</TableHead>
                  <TableHead>Operação</TableHead>
                  <TableHead className="text-right">Débito</TableHead>
                  <TableHead className="text-right">Crédito</TableHead>
                  <TableHead className="text-right">Saldo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.linhas.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    Nenhum lançamento no período.
                  </TableCell></TableRow>
                ) : data.linhas.map((l) => (
                  <TableRow key={`${l.lancamentoId}-${l.dtMovimento}-${l.debito}-${l.credito}`}>
                    <TableCell>{new Date(l.dtMovimento).toLocaleDateString("pt-BR")}</TableCell>
                    <TableCell className="font-mono">{l.lancamentoId}</TableCell>
                    <TableCell className="text-xs">
                      {TIPO_OPERACAO_LABEL[l.tipoOperacao as TipoOperacaoContabil] ?? "—"} #{l.operacaoId}
                    </TableCell>
                    <TableCell className="text-right">{l.debito > 0 ? fmtCur(l.debito) : "—"}</TableCell>
                    <TableCell className="text-right">{l.credito > 0 ? fmtCur(l.credito) : "—"}</TableCell>
                    <TableCell className="text-right font-semibold">{fmtCur(l.saldo)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
